import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { FirLocalAcctDescService } from './fir-local-acct-desc.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-fir-local-acct-desc',
    templateUrl: './fir-local-acct-desc.component.html',
    styleUrls: ['./fir-local-acct-desc.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FirLocalAcctDescComponent implements OnInit, OnDestroy {
  private static readonly bridgeHostSelector = 'gtw-fir-local-acct-desc-ce';

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private isInitialized = false;
  private saveEpoch = 0;
  private readonly contextSubs = new Subscription();
  private contextEpoch = 0;
  private readonly destroyed$ = new Subject<void>();

  modalTitle = 'Local-Acct-Desc';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = false;

  firLocalAcctDetail: any = {
    LOCAL_ACCT: '',
    LOCAL_ACCT_DESC: '',
    LOCAL_ACCT_DESC_NEW: '',
  };

  isFormDirty = false;
  isFormValid = true;

  baseURLs: GtwElementsBaseUrlsModel = {
    admin: '',
    api: '',
    custom: '',
    efile: '',
    pdfEngine: '',
    bulkPdfEngine: '',
  };

  globalService: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};
  filterParams: any = {};

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private firLocalAcctDescService: FirLocalAcctDescService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.resetAndTryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.resetAndTryInit();
      })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.resetAndTryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.contextEpoch++;
    this.saveEpoch++;
    this.destroyed$.next();
    this.destroyed$.complete();
    this.contextSubs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? '',
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc as GtwElementsBaseUrlsModel;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
  }

  private resetAndTryInit(): void {
    this.contextEpoch++;
    this.isInitialized = false;
    this.applyWorkspaceContext();
    this.tryInit();
    this.cdr.markForCheck();
  }

  private resolveApiBase(): string {
    return String(
      this.GENERAL_CONFIG?.base_url ||
        this.GENERAL_CONFIG?.api ||
        this.baseURLs?.api ||
        this.baseURLs?.admin ||
        ''
    );
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }
    if (!this.globalService || !this.GENERAL_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }
    if (!this.resolveApiBase()) {
      return;
    }
    this.isInitialized = true;
    this.init();
  }

  init(): void {
    try {
      this.setupFormData();
      this.showFormLoader = false;
      this.cdr.detectChanges();
    } catch (e) {
      console.error('Error in component init:', e);
      this.showFormLoader = false;
      this.isInitialized = false;
      this.cdr.detectChanges();
    }
  }

  private setupFormData(): void {
    this.firLocalAcctDetail = {
      LOCAL_ACCT: this.rowData.LOCAL_ACCT || '',
      LOCAL_ACCT_DESC: this.rowData.LOCAL_ACCT_DESC || '',
      LOCAL_ACCT_DESC_NEW: this.rowData.LOCAL_ACCT_DESC || '',
    };
  }

  validateForm(): boolean {
    const newDesc = this.firLocalAcctDetail.LOCAL_ACCT_DESC_NEW;

    if (!newDesc || newDesc.trim() === '') {
      this.isFormValid = false;
      return false;
    }

    if (newDesc.length > 100) {
      this.isFormValid = false;
      return false;
    }

    this.isFormValid = true;
    return true;
  }

  onDescriptionChange(): void {
    this.isFormDirty = true;
    this.validateForm();
    this.cdr.detectChanges();
  }

  save(): void {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar(
        'Please wait while we save this request',
        'gtw-snackbar--yellow',
        true,
        4
      );
      return;
    }

    if (!this.validateForm()) {
      this.gtwSnackbarService.openSnackBar(
        'Please fill all required fields correctly',
        'gtw-snackbar--yellow',
        true,
        4
      );
      return;
    }

    const epoch = ++this.saveEpoch;
    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const returnObj: any = {
      object_id: this.rowData.object_id,
      local_acct_key: this.rowData.LOCAL_ACCT_KEY,
      old_local_acct_desc: this.rowData.LOCAL_ACCT_DESC,
      new_local_acct_desc: this.firLocalAcctDetail.LOCAL_ACCT_DESC_NEW,
      group_object_key: this.rowData.GROUP_OBJ_KEY,
      tax_year: this.rowData.TAX_YEAR,
      source_system: 'F',
    };

    const firLocalAcctDescDetails = [returnObj];

    this.firLocalAcctDescService
      .saveFirLocalAcctDesc(
        this.GENERAL_CONFIG.base_url || this.baseURLs.api,
        returnObj,
        firLocalAcctDescDetails,
        this.globalService,
        this.filterParams
      )
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (response: any) => {
          if (epoch !== this.saveEpoch) {
            return;
          }
          this.isSaveClicked = false;
          this.crudLoading = false;

          if (response.errorMessage && response.errorMessage !== 'null') {
            this.gtwSnackbarService.openSnackBar(String(response.errorMessage), 'gtw-snackbar--red', true, 4);
          } else {
            this.gtwSnackbarService.openSnackBar(
              'Local Acct Desc has been successfully save/updated',
              'gtw-snackbar--green',
              true,
              4
            );
            this.requestGridUpdate.emit('refresh');
          }
          this.cdr.detectChanges();
        },
        () => {
          if (epoch !== this.saveEpoch) {
            return;
          }
          this.isSaveClicked = false;
          this.crudLoading = false;
          this.gtwSnackbarService.openSnackBar(
            'Error occurred while saving. Please try again.',
            'gtw-snackbar--red',
            true,
            4
          );
          this.cdr.detectChanges();
        }
      );
  }

  reset(): void {
    this.firLocalAcctDetail = {
      LOCAL_ACCT: this.rowData.LOCAL_ACCT || '',
      LOCAL_ACCT_DESC: this.rowData.LOCAL_ACCT_DESC || '',
      LOCAL_ACCT_DESC_NEW: this.rowData.LOCAL_ACCT_DESC || '',
    };
    this.isFormDirty = false;
    this.isFormValid = true;
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
